import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '@/utils/utils';
import { FLOATING, OVERLINE } from '@/components/ui/recipes';

// Lureo's floating panel: everything that opens from a control and floats beside it (the connection, story health,
// theme and notification panels, pickers). It opens and closes the same way everywhere: a click on its trigger, Escape,
// a click anywhere else including in the 3D view; and it sits the same distance from its trigger, its header, body,
// sections and rows spaced the same. Nothing that uses it sets its own surface, padding or position.

/** A click inside the 3D view (Unity's iframe) never reaches the page, so the editor announces it with this event. */
const UNITY_CLICKED = 'unity-clicked';

interface PanelProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

/** The anchor's element, so a click on the anchor (a search field the panel lists results for) doesn't count as outside. */
const AnchorContext = React.createContext<{ anchor: HTMLElement | null; setAnchor: (node: HTMLElement | null) => void } | null>(null);

/** The panel and its trigger; open state is its own unless `open` is passed. */
export function Panel({ open: openProp, defaultOpen = false, onOpenChange, children }: PanelProps) {
  const [openState, setOpenState] = React.useState(defaultOpen);
  const open = openProp ?? openState;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setOpenState(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener(UNITY_CLICKED, close);
    return () => window.removeEventListener(UNITY_CLICKED, close);
  }, [open, setOpen]);

  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const anchorContext = React.useMemo(() => ({ anchor, setAnchor }), [anchor]);
  return (
    <AnchorContext.Provider value={anchorContext}>
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        {children}
      </PopoverPrimitive.Root>
    </AnchorContext.Provider>
  );
}

export const PanelTrigger = PopoverPrimitive.Trigger;
/** What the panel sits beside when no trigger opens it (a search field); clicks on it leave the panel open. */
export const PanelAnchor = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor>>((props, ref) => {
  const setAnchor = React.useContext(AnchorContext)?.setAnchor;
  return (
    <PopoverPrimitive.Anchor
      ref={(node: HTMLDivElement | null) => {
        setAnchor?.(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      }}
      {...props}
    />
  );
});
PanelAnchor.displayName = 'PanelAnchor';
export const PanelClose = PopoverPrimitive.Close;

/** The panel's width: sm for a short list, md for most panels, lg for a panel holding an editor, xl for an editor whose rows carry a value and its actions. */
const WIDTHS = { sm: 'w-72', md: 'w-80', lg: 'w-96', xl: 'w-[28rem]' } as const;

export const PanelContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    width?: keyof typeof WIDTHS;
    /** Where it portals: another window's body (the detached timeline) instead of this one's. */
    container?: HTMLElement | null;
  }
>(({ width = 'md', align = 'center', side = 'bottom', sideOffset = 10, container, className, children, onInteractOutside, ...props }, ref) => {
  const anchor = React.useContext(AnchorContext)?.anchor;
  return (
  <PopoverPrimitive.Portal container={container}>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      side={side}
      sideOffset={sideOffset}
      collisionPadding={16}
      onInteractOutside={(event) => {
        if (anchor?.contains(event.target as Node)) event.preventDefault();
        // The 3D view takes focus as the pointer passes over it; only a click there (UNITY_CLICKED) closes the panel
        if (event.type === 'dismissableLayer.focusOutside' && (event.target as Element | null)?.tagName === 'IFRAME') event.preventDefault();
        onInteractOutside?.(event);
      }}
      className={cn(
        'z-lureo-layer-overlay flex max-h-[min(80vh,var(--radix-popover-content-available-height))] flex-col overflow-hidden rounded-lureo-radius-lg font-lureo-font-ui',
        FLOATING,
        WIDTHS[width],
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:ease-lureo-easing-enter data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
        className,
      )}
      {...props}
    >
      {children}
    </PopoverPrimitive.Content>
  </PopoverPrimitive.Portal>
  );
});
PanelContent.displayName = 'PanelContent';

/** The header bar: the panel's title, and what it reports or its actions on the right. */
export function PanelHeader({ title, aside }: { title: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <div className="flex min-h-12 shrink-0 items-center justify-between gap-3 border-b-lureo-border-width border-lureo-line px-4 py-2">
      <span className="text-lureo-14 font-semibold leading-tight text-lureo-ink">{title}</span>
      {aside && <div className="-mr-1.5 flex shrink-0 items-center gap-1 text-lureo-13">{aside}</div>}
    </div>
  );
}

/**
 * The panel's content, scrolling when taller than the screen allows: sections spaced apart, or (list) rows edge to edge,
 * each row bringing its own padding.
 */
export function PanelBody({ children, list = false }: { children: React.ReactNode; list?: boolean }) {
  return <div className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto', list ? 'gap-0.5 p-1.5' : 'gap-4 p-4')}>{children}</div>;
}

/** A group of rows under a small heading (the overline, or plain words: a popover's Recent events), with an optional note on the heading's right. */
export function PanelSection({ title, aside, heading = 'overline', children }: { title?: React.ReactNode; aside?: React.ReactNode; heading?: 'overline' | 'plain'; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      {(title || aside) && (
        <div className="flex items-center justify-between gap-2">
          {title && <h4 className={heading === 'plain' ? 'font-lureo-font-ui text-lureo-12 font-medium leading-4 text-lureo-ink-subtle' : OVERLINE.group}>{title}</h4>}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/** One fact: its name on the left, its value on the right, and an optional note under it. */
export function PanelRow({ label, children, note }: { label: React.ReactNode; children?: React.ReactNode; note?: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <div className="flex min-h-7 items-center justify-between gap-3">
        <span className="min-w-0 truncate text-lureo-13 text-lureo-ink">{label}</span>
        {children && <span className="flex shrink-0 items-center gap-2 text-lureo-13">{children}</span>}
      </div>
      {note && <div className="pb-1 text-lureo-12 leading-[1.4] text-lureo-ink-subtle">{note}</div>}
    </div>
  );
}

/** What a panel or section says when it has nothing to show. */
export function PanelEmpty({ children }: { children: React.ReactNode }) {
  return <p className="font-lureo-font-prose text-lureo-13 text-lureo-ink-subtle">{children}</p>;
}
